"use client";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import Language from "@/domain/enums/Language";
import useGetDateFnsLocale from "@/hooks/utils/useGetDateFnsLocale";
import i18n from "@/i18n";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { eventFormTone, type EventFormTone } from "./eventFormTone";

type EventDayPickerProps = {
    id: string;
    date?: Date;
    setDate: (date?: Date) => void;
    tone?: EventFormTone;
};

const EventDayPicker = ({
    id,
    date,
    setDate,
    tone = "dark",
}: EventDayPickerProps) => {
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation();
    const locale = useGetDateFnsLocale(i18n.language as Language);

    return (
        <div className="flex flex-col gap-1">
            <Popover onOpenChange={setOpen} open={open}>
                <PopoverTrigger asChild>
                    <Button
                        className={cn(
                            "justify-between rounded-md border px-3 py-1.5",
                            eventFormTone[tone].border,
                        )}
                        id={`${id}-date-picker`}
                        size="blank"
                        variant="invisible"
                    >
                        {date
                            ? // The app's language, not the browser's (« 13/10/2026 », not « 10/13/2026 »).
                              date.toLocaleDateString(i18n.language)
                            : t("dashboard.widgets.calendar.event.selectDate")}
                        <ChevronDownIcon className="h-4 w-4" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="w-auto overflow-hidden p-0"
                >
                    <Calendar
                        captionLayout="dropdown"
                        locale={locale}
                        mode="single"
                        onSelect={(selectedDate) => {
                            if (selectedDate) {
                                setDate(selectedDate);
                                setOpen(false);
                            }
                        }}
                        selected={date}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default EventDayPicker;
